import AlarmAddIcon from '@mui/icons-material/AlarmAdd';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import {
    Autocomplete,
    AutocompleteRenderGetTagProps,
    Box,
    Checkbox,
    Chip,
    FormControl,
    InputLabel,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { AlertOrReport, CustomerUserDetails, DeviceV3 } from '../../../backendsdk';
import {
    TrackedButton as Button,
    TrackedIconButton as IconButton,
    TrackedMenuItem as MenuItem,
} from '../../../components/TrackedComponents';
import useApi from '../../../hooks/api';
import { ExtendedFleetDriver, getDriverDisplayName } from '../../../hooks/drivers';
import useProfile from '../../../hooks/profile';
import { alertsPageDefs } from '../../../utils/Pages';
import { localeSort, uniq } from '../../../utils/Str';
import { EMPTY_ALERT } from './ReportComponent';
import { checkedIcon, icon } from './ReportContent';
import { DEVICE_FILTER_OPTIONS, FILTER_AVAILABLE, REPORT_TYPE_OPTIONS } from './ReportDetails';
import {
    AccidentReportForm,
    Alert,
    AlertReport,
    DeviceWellnessForm,
    GeofenceEventForm,
    GetClipCompletionAlertForm,
    MalfunctionsReportForm,
    SafetyEventForm,
} from './ReportOptions';
import ScheduledReportsComponent from './ScheduledReports';

interface AlertComponentProps {
    alertType: string;
    setSelectedReportType: (reportType: string | null) => void;
    devices: DeviceV3[];
    drivers: ExtendedFleetDriver[];
    driverSubFleets: string[];
    users: CustomerUserDetails[];
    geofences: Record<string, string>;
    setAlert: CallableFunction;
    scheduledAlerts: AlertReport[];
    isLoadingScheduled: boolean;
}

const AlertComponent: React.FC<AlertComponentProps> = (props: AlertComponentProps) => {
    const empty_alert = JSON.parse(
        JSON.stringify({
            ...EMPTY_ALERT,
            report_type: props.alertType,
        }),
    );
    const [details, setDetails] = useState<Alert>(empty_alert);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { api } = useApi();
    const { t } = useTranslation();
    const { profile } = useProfile();
    const history = useHistory();
    const queryClient = useQueryClient();

    const ALLOW_VIEW_ALL_SUB_FLEETS = [...alertsPageDefs.permissions.L1];

    const keys = details.filters == undefined ? [] : Object.keys(details.filters);
    const deviceFilterKey =
        Object.keys(DEVICE_FILTER_OPTIONS).find((key) => keys.includes(key)) ?? DEVICE_FILTER_OPTIONS.ALL;

    const filteredDeviceFilterOptions = DEVICE_FILTER_OPTIONS;
    if (!ALLOW_VIEW_ALL_SUB_FLEETS.includes(profile.role) && profile.sub_fleets.length === 1) {
        delete filteredDeviceFilterOptions.DEPARTMENT;
    }

    const deviceFilterOptions = useMemo(() => {
        const options: Record<string, string[]> = { [DEVICE_FILTER_OPTIONS.ALL]: ['ALL'] };
        const filteredDevices =
            ALLOW_VIEW_ALL_SUB_FLEETS.includes(profile.role) || profile.sub_fleets.length === 0
                ? props.devices
                : props.devices.filter((d) => profile.sub_fleets.includes(d.device.customer_sub_fleet));
        options[DEVICE_FILTER_OPTIONS.LICENSE_PLATE] = filteredDevices
            .map((d) => d.device.license_plate)
            .filter(uniq)
            .sort(localeSort);
        options[DEVICE_FILTER_OPTIONS.DEPARTMENT] = ['SAFETY_EVENTS', 'GEOFENCE_EVENTS'].includes(props.alertType)
            ? props.driverSubFleets
            : props.devices
                  .map((d) => d.device.customer_sub_fleet ?? '')
                  .filter(uniq)
                  .sort(localeSort);
        const showDriverCode = profile.customer.settings.show_driver_code ?? false;
        options[DEVICE_FILTER_OPTIONS.DRIVER] = props.drivers
            .filter((d) => !d.is_auto_generated && d.active)
            .map((d) => getDriverDisplayName(d, showDriverCode))
            .filter(uniq)
            .sort(localeSort);

        return options;
    }, [deviceFilterKey, profile, props.devices, props.drivers, props.alertType]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleChange = (target: string, value: any) => {
        setDetails((prev) => {
            const newDetails = Object.fromEntries(
                Object.entries(prev).map(([key, origin_value]) => [key, key === target ? value : origin_value]),
            );
            newDetails[target] = value;
            return newDetails as Alert;
        });
    };

    const handleAdd = (details: Alert) => {
        setIsLoading(true);
        api.apiV0ReportPost({ alertOrReport: details as AlertOrReport })
            .then((res) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                queryClient.setQueryData(['reports'], (old: any) => {
                    if (old !== undefined) {
                        if (details.pk === 0) {
                            return { ...old, alerts: [...old.alerts, res.data] };
                        }
                        return {
                            ...old,
                            alerts: old.alerts.map((r: AlertReport) => (r.pk === res.data.pk ? res.data : r)),
                        };
                    }
                });
                setDetails(
                    JSON.parse(
                        JSON.stringify({
                            ...EMPTY_ALERT,
                            report_type: props.alertType,
                        }),
                    ),
                );
            })
            .catch(() =>
                props.setAlert({ message: t('content.reports.error_alert.save'), type: 'error', duration: 6000 }),
            )
            .finally(() => setIsLoading(false));
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                maxWidth: '1200px',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    minHeight: 0,
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: 0,
                        flexShrink: 0,
                        mb: 1.5,
                    }}
                >
                    <IconButton
                        id="btn-back"
                        size="small"
                        onClick={() => {
                            history.push('/alerts');
                            props.setSelectedReportType(null);
                        }}
                        sx={{ mr: 0.5 }}
                    >
                        <ArrowBackIosNewIcon />
                    </IconButton>
                    <Typography variant="h5">
                        {t(`content.reports.form.type.${props.alertType.toLocaleLowerCase()}`)}
                    </Typography>
                </Box>
                <Typography variant="overline" sx={{ lineHeight: 1 }}>
                    {t('content.reports.add_alert')}
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        flexShrink: 0,
                        mb: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', flexGrow: 1 }}>
                        {props.alertType === REPORT_TYPE_OPTIONS.GET_CLIP_COMPLETION ? (
                            <>
                                <Typography variant="caption" display="block" sx={{ width: '100%', mb: 1, mt: 0.5 }}>
                                    {t('content.reports.form.filter.get_clip_completion_help')}
                                </Typography>
                                <GetClipCompletionAlertForm
                                    handleChange={handleChange}
                                    details={details}
                                    users={props.users}
                                />
                            </>
                        ) : (
                            <>
                                <FormControl fullWidth sx={{ width: '200px', flexShrink: 0, mr: 1, mt: 1.5 }}>
                                    <InputLabel id="filter-vehicles-dropdown-label">
                                        {t('content.reports.form.filter.filters_header')}
                                    </InputLabel>
                                    <Select
                                        id="filter-vehicles-dropdown"
                                        data-testid="filter-vehicles-dropdown"
                                        labelId="filter-vehicles-dropdown-label"
                                        fullWidth
                                        size="small"
                                        label={t('content.reports.form.filter.filters_header')}
                                        value={deviceFilterKey}
                                        onChange={(e: SelectChangeEvent) => {
                                            const newFilter = { ...details.filters };
                                            for (const key of Object.keys(DEVICE_FILTER_OPTIONS)) {
                                                delete newFilter[key];
                                            }
                                            newFilter[e.target.value] = [];
                                            handleChange('filters', newFilter);
                                        }}
                                    >
                                        {Object.keys(DEVICE_FILTER_OPTIONS)
                                            .filter((option) => FILTER_AVAILABLE[option].includes(details.report_type))
                                            .map((option) => {
                                                return (
                                                    <MenuItem
                                                        key={option}
                                                        value={option}
                                                        id={option}
                                                        disabled={!deviceFilterOptions[option]?.length}
                                                    >
                                                        {t(`content.reports.form.filter.${option.toLocaleLowerCase()}`)}
                                                    </MenuItem>
                                                );
                                            })}
                                    </Select>
                                </FormControl>
                                {deviceFilterKey == DEVICE_FILTER_OPTIONS.ALL ? null : (
                                    <Autocomplete
                                        id="filter-vehicles-values-autocomplete"
                                        data-testid="filter-vehicles-values-autocomplete"
                                        multiple
                                        disableCloseOnSelect
                                        size="small"
                                        options={deviceFilterOptions[deviceFilterKey] || []}
                                        renderOption={(props, option, { selected }) => (
                                            <li {...props} style={{ padding: 0 }}>
                                                <Checkbox
                                                    id={`${option}-checkbox`}
                                                    icon={icon}
                                                    checkedIcon={checkedIcon}
                                                    checked={selected}
                                                    size="small"
                                                />
                                                <Typography sx={{ pt: 0.5 }}>{option}</Typography>
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t(
                                                    `content.reports.form.filter.${deviceFilterKey.toLocaleLowerCase()}`,
                                                )}
                                            />
                                        )}
                                        renderTags={(
                                            value: Array<string>,
                                            getTagProps: AutocompleteRenderGetTagProps,
                                        ) => {
                                            return (
                                                <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                                                    <Chip
                                                        size="small"
                                                        label={<Typography fontSize={12}>{value[0]}</Typography>}
                                                        {...getTagProps({ index: 0 })}
                                                        sx={{ borderRadius: 1 }}
                                                        onDelete={undefined}
                                                    />
                                                    {value.length > 1 ? (
                                                        <Typography variant="body2">{`+${
                                                            value.length - 1
                                                        }`}</Typography>
                                                    ) : null}
                                                </Box>
                                            );
                                        }}
                                        value={details.filters[deviceFilterKey] as string[]}
                                        onChange={(_event, value) => {
                                            const newFilter = { ...details.filters };
                                            delete newFilter[DEVICE_FILTER_OPTIONS.LICENSE_PLATE];
                                            delete newFilter[DEVICE_FILTER_OPTIONS.DEPARTMENT];
                                            newFilter[deviceFilterKey] = value;
                                            handleChange('filters', newFilter);
                                        }}
                                        sx={{
                                            minWidth: '200px',
                                            flexShrink: 0,
                                            mr: 1,
                                            mt: 1.5,
                                            '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' },
                                            '& .MuiInputBase-input': {
                                                minWidth: '0px !important',
                                                paddingLeft: !!details.filters[deviceFilterKey]?.length
                                                    ? '0px !important'
                                                    : '6px',
                                                paddingRight: !!details.filters[deviceFilterKey]?.length
                                                    ? '0px !important'
                                                    : '4px',
                                            },
                                        }}
                                    />
                                )}
                                {details.report_type == REPORT_TYPE_OPTIONS.SAFETY_EVENTS && (
                                    <SafetyEventForm handleChange={handleChange} details={details} />
                                )}
                                {details.report_type == REPORT_TYPE_OPTIONS.GEOFENCE_EVENTS && (
                                    <GeofenceEventForm
                                        geofences={props.geofences}
                                        handleChange={handleChange}
                                        details={details}
                                    />
                                )}
                                {details.report_type == REPORT_TYPE_OPTIONS.DEVICE_WELLNESS && (
                                    <DeviceWellnessForm handleChange={handleChange} details={details} />
                                )}
                                {details.report_type == REPORT_TYPE_OPTIONS.ACCIDENTS_REPORT && (
                                    <AccidentReportForm handleChange={handleChange} details={details} />
                                )}
                                {details.report_type == REPORT_TYPE_OPTIONS.MALFUNCTIONS && (
                                    <MalfunctionsReportForm handleChange={handleChange} details={details} />
                                )}
                            </>
                        )}
                    </Box>
                    <Button
                        id="btn-add-alert"
                        data-testid="btn-add-alert"
                        variant="contained"
                        color="secondary"
                        startIcon={<AlarmAddIcon />}
                        disabled={isLoading || props.isLoadingScheduled}
                        onClick={() => handleAdd(details)}
                        sx={{ flexShrink: 0, height: '40px' }}
                    >
                        {t('content.reports.add_alert')}
                    </Button>
                </Box>
                <Typography variant="overline" sx={{ lineHeight: 1, mb: 1 }}>
                    {t('content.reports.scheduled_alerts')}
                </Typography>
                <ScheduledReportsComponent
                    type="ALERTS"
                    reports={props.scheduledAlerts}
                    devices={props.devices}
                    drivers={props.drivers}
                    geofences={props.geofences}
                    users={props.users}
                    isLoading={props.isLoadingScheduled}
                    reportType={props.alertType}
                />
            </Box>
        </Box>
    );
};

export default AlertComponent;
